import { capitalize } from 'lodash';
import { useLocation, useMatches } from 'react-router-dom';

import { useMapUiContext } from '@/context/MapUiContext';

export type ChatContext = {
    currentPage?: string;
    spotterId?: string;
    viewportSpotterIds?: string[];
};

// SpotterMap only tracks spotterIdsInViewport while on the acoustics map currently,
// reporting it for the fleet map too would just return stale detections.
const MAP_PAGES = new Set(['acoustics']);

const getCurrentPage = (pathname: string, params: Record<string, string | undefined>): string => {
    const routePattern = Object.values(params).reduce<string>(
        (path, param) => (param ? path.replace(`/${param}`, '') : path),
        pathname
    );

    const [firstSegment] = routePattern.replace(/^\//, '').split('/');
    return firstSegment || 'spotter-map';
};

export const useChatContext = (): ChatContext => {
    const { pathname } = useLocation();
    const matches = useMatches();
    const { spotterIdsInViewport } = useMapUiContext();

    const params: Record<string, string | undefined> = matches[matches.length - 1]?.params ?? {};
    const currentPage = getCurrentPage(pathname, params);

    const context: ChatContext = { currentPage };

    if (params.spotterId) {
        context.spotterId = params.spotterId;
    }

    // MapUiContext's spotterIdsInViewport isn't reset when the map unmounts, so it's only
    // meaningful to report while a map page is actually the one on screen.
    if (MAP_PAGES.has(currentPage) && spotterIdsInViewport.length > 0) {
        context.viewportSpotterIds = [...spotterIdsInViewport];
    }

    return context;
};

const PAGE_LABELS: Record<string, string> = {
    'spotter-map': 'Spotter map',
    acoustics: 'Acoustics map',
    spotter: 'Device detail',
    grid: 'Device detail',
    account: 'Account',
    api: 'API',
    billing: 'Billing',
    telemetry: 'Telemetry',
    payment: 'Payment',
    setup: 'Spotter setup',
};

export const describeChatContext = (context: ChatContext): string => {
    const pageLabel = context.currentPage
        ? (PAGE_LABELS[context.currentPage] ?? capitalize(context.currentPage))
        : 'Dashboard';

    if (context.spotterId) {
        return `${pageLabel} · ${context.spotterId}`;
    }

    if (context.viewportSpotterIds && context.viewportSpotterIds.length > 0) {
        const deviceCount = context.viewportSpotterIds.length;
        return `${pageLabel} · ${deviceCount} device${deviceCount === 1 ? '' : 's'} in view`;
    }

    return pageLabel;
};
